import {
  CheckIconProps,
  ColorSwatch,
  Combobox,
  ComboboxProps,
  Flex,
  Group,
  Input,
  InputBase,
  ScrollArea,
  useCombobox,
} from "@mantine/core";
import { IMultiOptionsColors } from "@mctypes/global.types";
import { IconCaretDownFilled, IconCheck } from "@tabler/icons-react";
import { useMemo, useState } from "react";

const iconProps: CheckIconProps = {
  color: "currentColor",
  opacity: 0.6,
  size: 18,
};

type McColorSelectProps = Omit<ComboboxProps, "onChange"> & {
  onChange: (value: string | null) => void;
  data: IMultiOptionsColors[];
  value: string | null;
  placeholder?: string;
};

export function McColorSelect({ data, onChange, value, placeholder, ...props }: McColorSelectProps) {
  const [search, setSearch] = useState<string>("");
  const [hasOpenedOnce, setHasOpenedOnce] = useState<boolean>(false);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: (eventSource) => {
      setHasOpenedOnce(true);

      if (eventSource === "keyboard") {
        combobox.selectActiveOption();
      } else {
        combobox.updateSelectedOptionIndex("active");
      }
    },
  });

  const currentItem = useMemo(() => data.find((entry) => entry.key === value) ?? null, [data, value]);

  const options = data
    .filter((item) => item.key.toLowerCase().includes(search.toLowerCase()))
    .map((item) => (
      <Combobox.Option value={item.key} key={item.key} active={item.key === value}>
        <Group flex="1" gap="xs" wrap="nowrap">
          <ColorSwatch size={10} color={item.color} />
          <Flex wrap="nowrap" flex={1}>
            {item.displayName}
          </Flex>
          {item.key === value && <IconCheck style={{ marginInlineStart: "auto" }} {...iconProps} />}
        </Group>
      </Combobox.Option>
    ));

  return (
    <Combobox
      store={combobox}
      resetSelectionOnOptionHover
      onOptionSubmit={(val) => {
        if (val !== value) {
          onChange(val);
        }
        combobox.closeDropdown();
      }}
      {...props}
    >
      <Combobox.Target>
        <InputBase
          styles={{
            input: {
              outline: "none",
              boxShadow: "none",
            },
          }}
          component="button"
          type="button"
          pointer
          rightSection={<IconCaretDownFilled size={17} color="var(--mc-icon-default)" />}
          rightSectionPointerEvents="none"
          onClick={() => combobox.toggleDropdown()}
        >
          {currentItem != null ? (
            <Group flex="1" gap="xs" wrap="nowrap">
              <ColorSwatch size={10} color={currentItem.color} />
              <Flex wrap="nowrap" flex={1}>
                {currentItem.displayName}
              </Flex>
            </Group>
          ) : (
            <Input.Placeholder>{placeholder}</Input.Placeholder>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        {data.length > 10 && (
          <Combobox.Search
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Søk..."
          />
        )}
        <Combobox.Options>
          {hasOpenedOnce && (
            <ScrollArea.Autosize mah={200} type="scroll">
              {options.length === 0 ? <Combobox.Empty>Ingen treff</Combobox.Empty> : options}
            </ScrollArea.Autosize>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
